import ExcelJS from 'exceljs';
import type { DuplicatePair } from './duplicate-detect';

// Excel-Export der Duplikat-Paare mit visuellem Header, Erklärungsblock,
// farbigem Header, Score-Farbcodierung, Zebra-Streifen und Hyperlinks.
// Layout so, dass die Datei auch ohne Rückfragen in einem Aufräum-Meeting
// „selbsterklärend" ist.

export type DuplicateExportOptions = {
  jiraBaseUrl: string;              // z.B. „https://schueco.atlassian.net"
  team: string;                     // aktueller Team-Filter oder '' für alle
  threshold: number;                // 0..100 (Prozent) — nur für Meta-Zeile
  doneWindow: number | 'all';       // wie in der UI
};

// Farbpalette — Schueco-nahe Blautöne + neutrales Farbfeedback für Score.
const COLORS = {
  headerBg:      'FF1E3A8A',   // dunkelblau (Titel-Balken)
  headerFg:      'FFFFFFFF',
  subHeaderBg:   'FFE0E7FF',   // hellblau (Erklärungsblock)
  subHeaderFg:   'FF1E3A8A',
  metaBg:        'FFF3F4F6',   // grau (Meta-Zeile)
  metaFg:        'FF374151',
  columnBg:      'FF334155',   // slate (Spalten-Header)
  columnFg:      'FFFFFFFF',
  zebraBg:       'FFF8FAFC',   // hell-grau
  border:        'FFCBD5E1',   // hellblau-grau
  scoreVeryHigh: 'FFDC2626',   // ≥90% rot
  scoreHigh:     'FFF97316',   // ≥80% orange
  scoreMed:      'FFF59E0B',   // ≥70% gelb
  scoreLow:      'FF10B981',   // <70% grün
  linkFg:        'FF1D4ED8',   // blau für Hyperlinks
};

const HEADERS = [
  { key: 'score',     header: 'Score %',              width: 10, align: 'center' as const },
  { key: 'shared',    header: 'Shared',               width:  9, align: 'center' as const },
  { key: 'uniqueA',   header: 'Unique A',             width:  9, align: 'center' as const },
  { key: 'uniqueB',   header: 'Unique B',             width:  9, align: 'center' as const },
  { key: 'team',      header: 'Team',                 width: 18, align: 'left'   as const },
  { key: 'type',      header: 'Issue Type',           width: 12, align: 'center' as const },
  { key: 'keyA',      header: 'Key A',                width: 14, align: 'left'   as const },
  { key: 'statusA',   header: 'Status A',             width: 15, align: 'left'   as const },
  { key: 'assigneeA', header: 'Assignee A',           width: 18, align: 'left'   as const },
  { key: 'summaryA',  header: 'Summary A',            width: 60, align: 'left'   as const },
  { key: 'keyB',      header: 'Key B',                width: 14, align: 'left'   as const },
  { key: 'statusB',   header: 'Status B',             width: 15, align: 'left'   as const },
  { key: 'assigneeB', header: 'Assignee B',           width: 18, align: 'left'   as const },
  { key: 'summaryB',  header: 'Summary B',            width: 60, align: 'left'   as const },
  { key: 'keywords',  header: 'Gemeinsame Keywords',  width: 42, align: 'left'   as const },
];

function issueField(row: Record<string, unknown> | undefined, key: string): string {
  if (!row) return '';
  const v = row[key];
  if (v == null) return '';
  if (Array.isArray(v)) return v.map(String).join(', ');
  return String(v);
}

function scoreColor(pct: number): string {
  if (pct >= 90) return COLORS.scoreVeryHigh;
  if (pct >= 80) return COLORS.scoreHigh;
  if (pct >= 70) return COLORS.scoreMed;
  return COLORS.scoreLow;
}

export async function exportDuplicatesXlsx(
  pairs: DuplicatePair[],
  opts: DuplicateExportOptions,
): Promise<void> {
  const baseUrl = (opts.jiraBaseUrl || 'https://schueco.atlassian.net').replace(/\/+$/, '');
  const browse = (key: string) => `${baseUrl}/browse/${encodeURIComponent(key)}`;

  const wb = new ExcelJS.Workbook();
  wb.creator = 'SchueCal Dashboard';
  wb.created = new Date();
  const ws = wb.addWorksheet('Duplikat-Paare', {
    views: [{ state: 'frozen', ySplit: 6, xSplit: 0 }],  // Header-Zeile bleibt sichtbar
    properties: { defaultRowHeight: 16 },
  });

  const nCols = HEADERS.length;
  const lastColLetter = ws.getColumn(nCols).letter;

  // ─── Zeile 1: Titel-Balken ───────────────────────────────────────────────
  ws.mergeCells(1, 1, 1, nCols);
  const titleCell = ws.getCell(1, 1);
  titleCell.value = 'SchueCal · Mögliche Duplikate — Gegenüberstellung offener Tickets';
  titleCell.font = { name: 'Calibri', size: 16, bold: true, color: { argb: COLORS.headerFg } };
  titleCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.headerBg } };
  ws.getRow(1).height = 32;

  // ─── Zeile 2–3: Erklärungsblock ─────────────────────────────────────────
  ws.mergeCells(2, 1, 2, nCols);
  const desc1 = ws.getCell(2, 1);
  desc1.value =
    'Jede Zeile ist ein Ticket-Paar mit lexikalisch ähnlicher Summary (Jaccard über normalisierte Tokens). ' +
    'Seite A ist immer ein offenes Ticket, Seite B ist offen ODER kürzlich abgeschlossen. ' +
    'Team + Issue-Type sind identisch (Pre-Filter); CAL↔CALEXT-Sync-Paare sind entfernt.';
  desc1.font = { name: 'Calibri', size: 10.5, color: { argb: COLORS.subHeaderFg } };
  desc1.alignment = { vertical: 'middle', horizontal: 'left', indent: 1, wrapText: true };
  desc1.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.subHeaderBg } };
  ws.getRow(2).height = 32;

  ws.mergeCells(3, 1, 3, nCols);
  const desc2 = ws.getCell(3, 1);
  desc2.value =
    'So liest man die Zeile:  Score % = Ähnlichkeit  ·  Shared = gemeinsame Tokens  ·  Unique A/B = nur in A bzw. B  ·  ' +
    'Gemeinsame Keywords = die 8 stärksten Übereinstimmungen  ·  Key A/B sind klickbar und öffnen das Ticket in JIRA.  ' +
    'Score-Ampel: 🟢 60–69 %  🟡 70–79 %  🟠 80–89 %  🔴 ≥ 90 %.';
  desc2.font = { name: 'Calibri', size: 10.5, color: { argb: COLORS.subHeaderFg } };
  desc2.alignment = { vertical: 'middle', horizontal: 'left', indent: 1, wrapText: true };
  desc2.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.subHeaderBg } };
  ws.getRow(3).height = 32;

  // ─── Zeile 4: Meta-Zeile (Filter-Kontext) ───────────────────────────────
  ws.mergeCells(4, 1, 4, nCols);
  const metaCell = ws.getCell(4, 1);
  metaCell.value =
    `Team-Filter:  ${opts.team || 'Alle Teams'}     ·     ` +
    `Schwelle:  ≥ ${opts.threshold} %     ·     ` +
    `Done-Fenster:  ${opts.doneWindow === 'all' ? 'Alle abgeschlossenen' : `≤ ${opts.doneWindow} Monate`}     ·     ` +
    `Paare:  ${pairs.length}     ·     ` +
    `Erstellt:  ${new Date().toLocaleString('de-DE')}`;
  metaCell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: COLORS.metaFg } };
  metaCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  metaCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.metaBg } };
  ws.getRow(4).height = 20;

  // Zeile 5: Abstands-Leerzeile
  ws.getRow(5).height = 6;

  // ─── Zeile 6: Spalten-Header ────────────────────────────────────────────
  const headerRow = ws.getRow(6);
  HEADERS.forEach((h, i) => {
    const col = ws.getColumn(i + 1);
    col.width = h.width;
    const c = headerRow.getCell(i + 1);
    c.value = h.header;
    c.font = { name: 'Calibri', size: 10.5, bold: true, color: { argb: COLORS.columnFg } };
    c.alignment = { vertical: 'middle', horizontal: h.align, wrapText: true };
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.columnBg } };
    c.border = {
      top:    { style: 'thin', color: { argb: COLORS.border } },
      bottom: { style: 'thin', color: { argb: COLORS.border } },
      left:   { style: 'thin', color: { argb: COLORS.border } },
      right:  { style: 'thin', color: { argb: COLORS.border } },
    };
  });
  headerRow.height = 26;

  // ─── Datenzeilen ─────────────────────────────────────────────────────────
  const firstDataRow = 7;
  pairs.forEach((p, idx) => {
    const a = p.a as unknown as Record<string, unknown>;
    const b = p.b as unknown as Record<string, unknown>;
    const keyA = issueField(a, 'Issue key');
    const keyB = issueField(b, 'Issue key');
    const pct  = Math.round(p.score * 1000) / 10;
    const rowIdx = firstDataRow + idx;
    const row = ws.getRow(rowIdx);
    const zebra = idx % 2 === 1;

    const values: (string | number)[] = [
      pct,
      p.sharedCount,
      p.uniqueA,
      p.uniqueB,
      issueField(a, 'Team Name'),
      issueField(a, 'Issue Type'),
      keyA,
      issueField(a, 'Status'),
      issueField(a, 'Assignee'),
      issueField(a, 'Summary'),
      keyB,
      issueField(b, 'Status'),
      issueField(b, 'Assignee'),
      issueField(b, 'Summary'),
      p.shared.join(', '),
    ];
    values.forEach((v, i) => { row.getCell(i + 1).value = v; });

    // Basis-Styling für die ganze Zeile
    HEADERS.forEach((h, i) => {
      const cell = row.getCell(i + 1);
      cell.alignment = { vertical: 'top', horizontal: h.align, wrapText: h.key.startsWith('summary') || h.key === 'keywords' };
      cell.font = { name: 'Calibri', size: 10 };
      cell.border = { bottom: { style: 'hair', color: { argb: COLORS.border } } };
      if (zebra) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.zebraBg } };
    });

    // Score-Zelle: farbig hinterlegt, weiße fette Schrift
    const scoreCell = row.getCell(1);
    scoreCell.value  = pct;
    scoreCell.numFmt = '0.0"%"';
    scoreCell.fill   = { type: 'pattern', pattern: 'solid', fgColor: { argb: scoreColor(pct) } };
    scoreCell.font   = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
    scoreCell.alignment = { vertical: 'middle', horizontal: 'center' };

    // Hyperlinks für Key A und Key B
    if (keyA) {
      const c = row.getCell(7);
      c.value = { text: keyA, hyperlink: browse(keyA), tooltip: 'In JIRA öffnen' };
      c.font = { name: 'Calibri', size: 10, bold: true, color: { argb: COLORS.linkFg }, underline: true };
    }
    if (keyB) {
      const c = row.getCell(11);
      c.value = { text: keyB, hyperlink: browse(keyB), tooltip: 'In JIRA öffnen' };
      c.font = { name: 'Calibri', size: 10, bold: true, color: { argb: COLORS.linkFg }, underline: true };
    }

    // Keywords als kursiv, damit sie visuell abgesetzt sind
    row.getCell(15).font = { name: 'Calibri', size: 9.5, italic: true, color: { argb: 'FF475569' } };

    // Zeilen-Höhe automatisch — leicht großzügig
    row.height = 30;
  });

  // ─── AutoFilter über den Datenbereich (inkl. Header) ────────────────────
  ws.autoFilter = {
    from: { row: 6, column: 1 },
    to:   { row: firstDataRow + pairs.length - 1, column: nCols },
  };

  // ─── Info-Sheet mit Farb-Legende und Feld-Erklärung ─────────────────────
  const info = wb.addWorksheet('Legende & Info');
  info.columns = [{ width: 22 }, { width: 80 }];
  info.getCell(1, 1).value = 'SchueCal · Duplikat-Export';
  info.mergeCells(1, 1, 1, 2);
  info.getCell(1, 1).font = { name: 'Calibri', size: 16, bold: true, color: { argb: COLORS.headerFg } };
  info.getCell(1, 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.headerBg } };
  info.getCell(1, 1).alignment = { vertical: 'middle', indent: 1 };
  info.getRow(1).height = 30;

  const legend: Array<[string, string, string?]> = [
    ['Feld',                'Bedeutung'],
    ['Score %',             'Jaccard-Ähnlichkeit der normalisierten Summary-Tokens (Schnitt / Vereinigung). 100% = identisch.'],
    ['Shared',              'Anzahl gemeinsamer Tokens in A und B.'],
    ['Unique A',            'Tokens die nur in Summary A vorkommen.'],
    ['Unique B',            'Tokens die nur in Summary B vorkommen.'],
    ['Key A / Key B',       'JIRA-Issue-Key. Klick öffnet das Ticket im Browser.'],
    ['Status A / B',        'JIRA-Status zum Export-Zeitpunkt.'],
    ['Assignee A / B',      'Aktueller Bearbeiter (JIRA-Feld).'],
    ['Summary A / B',       'Titel des Tickets. Grundlage für die Ähnlichkeits-Erkennung.'],
    ['Gemeinsame Keywords', 'Bis zu 8 stärkste Token-Übereinstimmungen — worauf die Ähnlichkeit fußt.'],
    ['',                    ''],
    ['Score-Ampel',         '🟢 60–69 %  ·  🟡 70–79 %  ·  🟠 80–89 %  ·  🔴 ≥ 90 %'],
    ['',                    ''],
    ['Was ist NICHT drin?', 'CAL↔CALEXT-Sync-Paare (100% identische Summary, unterschiedliche Projekte). Diese werden separat behandelt.'],
    ['',                    ''],
    ['Vorgehen im Meeting', '1) Nach Score absteigend sortieren (rot zuerst).  ' +
                            '2) Pro Paar: Ist es fachlich das gleiche Anliegen?  ' +
                            '3) Wenn ja: eines aufmachen als „Duplikat von", das andere schließen.  ' +
                            '4) Wenn nein: Kommentar in JIRA warum es KEIN Duplikat ist (verhindert wiederkehrende Treffer).'],
  ];
  legend.forEach((row, i) => {
    const r = info.getRow(2 + i);
    r.getCell(1).value = row[0];
    r.getCell(2).value = row[1];
    r.getCell(1).font = { name: 'Calibri', size: 11, bold: i === 0 };
    r.getCell(2).font = { name: 'Calibri', size: 10.5 };
    r.getCell(1).alignment = { vertical: 'top' };
    r.getCell(2).alignment = { vertical: 'top', wrapText: true };
    r.height = row[1].length > 80 ? 44 : 20;
    if (i === 0) {
      r.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.columnBg } };
      r.getCell(2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.columnBg } };
      r.getCell(1).font = { name: 'Calibri', size: 11, bold: true, color: { argb: COLORS.columnFg } };
      r.getCell(2).font = { name: 'Calibri', size: 11, bold: true, color: { argb: COLORS.columnFg } };
    }
  });

  // ─── Datei erzeugen & Download auslösen ──────────────────────────────────
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const stamp = new Date().toISOString().slice(0, 10);
  const slug = (opts.team || 'alle-teams').replace(/[^\w-]+/g, '-').toLowerCase();
  const filename = `schuecal-duplikate_${slug}_ge${opts.threshold}pct_${stamp}.xlsx`;
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);

  // lastColLetter wird nicht wirklich für Zellen-Referenzen gebraucht — hier
  // nur als Sanity-Check, damit ExcelJS-Interne Range-Berechnungen sauber
  // bleiben (verhindert Tree-Shaking der Column-Metadaten).
  void lastColLetter;
}
